import { useIntl } from "react-intl";
import { NavLink, useLocation } from "react-router-dom";
import HomeIcon from "../../../../../assets/image/home.svg";
import CookbookIcon from "../../../../../assets/image/cookbook.svg";
import MenuIcon from "../../../../../assets/image/meus-menus.svg";
import CreateRecipeIcon from "../../../../../assets/image/criar-receita.svg";
import CreateMenuIcon from "../../../../../assets/image/novo-menu.svg";
import EquipmentIcon from "../../../../../assets/image/equipamentos.svg";
import UserIcon from "../../../../../assets/image/usuarios.svg";
import AnalyticsIcon from "../../../../../assets/image/analytics.svg";
import CompaniesIcon from "../../../../../assets/image/companhias.svg";
import API from "../../../../../assets/image/API.svg";
import InfoIOK from "../../../../../assets/image/info iok.svg";
import { LinksList, ListItem, Icon, Divider } from "./style";
import { db } from "../../../../../db/db";
import { useLiveQuery } from "dexie-react-hooks";
import ModalConfigs from "../../../../Modals/ModalConfigs";
import { useState } from "react";
import ModalEditAccount from "../../../../Modals/ModalEditAccount";
import { FaExternalLinkAlt } from "react-icons/fa";

type LinksProps = {
  hidden: boolean;
  handleHideNavbar: () => void;
};

type NavItemKey =
  | "analytics"
  | "createCookbook"
  | "createMenu"
  | "home"
  | "cookbook"
  | "menu"
  | "equipment"
  | "users"
  | "information"
  | "companies";

const LinksWeb = (props: LinksProps) => {
  const intl = useIntl();
  const userDb = useLiveQuery(() => db.user.toArray());
  const userConfigDb = useLiveQuery(() => db.userConfigs.toArray());
  const companyDb = useLiveQuery(() => db.company.toArray());
  const location = useLocation();
  const [showModalConfigs, setShowModalConfigs] = useState(false);
  const [showModalEditAccount, setShowModalEditAccount] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const currentUserConfig = userConfigDb?.[0];
  const permissions = Array.isArray(currentUserConfig?.permission)
    ? (currentUserConfig?.permission ?? [])
    : [];
  const isAnalyticsCompany = companyDb?.[0]?.companyType === "Analytics";
  const hasCookbookPermission = permissions.some(
    (permission) => permission.permission === "cookbook",
  );
  const hasMenusPermission = permissions.some(
    (permission) => permission.permission === "menus",
  );
  const hasEquipmentPermission = permissions.some(
    (permission) => permission.permission === "equipment",
  );
  const hasUsersPermission = permissions.some(
    (permission) => permission.permission === "users",
  );
  const explicitActiveItem = (
    location.state as { activeNav?: NavItemKey } | null
  )?.activeNav;

  const routeActiveItem = (): NavItemKey | null => {
    if (location.pathname.startsWith("/cookbook")) return "cookbook";
    if (location.pathname.startsWith("/menu")) return "menu";
    if (location.pathname.startsWith("/equipment")) return "equipment";
    if (location.pathname.startsWith("/users")) return "users";
    if (location.pathname.startsWith("/information")) return "information";
    if (location.pathname.startsWith("/analytics")) return "analytics";
    if (location.pathname.startsWith("/companies")) return "companies";
    if (location.pathname.startsWith("/home")) return "home";

    return null;
  };

  const activeItem = explicitActiveItem ?? routeActiveItem();
  const getActiveClassName = (item: NavItemKey) =>
    activeItem === item ? "active" : "";

  const redirectHomePage = (companyType: string): string => {
    switch (companyType) {
      case "Analytics":
        return "/analytics";
      case "Suporte":
        return "/home/support";
      case "Master":
        return "/home/master";
      default:
        return "/home";
    }
  };

  return (
    <>
      {showModalConfigs && (
        <div className="modal">
          <ModalConfigs
            onCancel={() => {
              setShowModalConfigs(false);
              setShowDropdown(!showDropdown);
            }}
            userConfigsDb={userConfigDb ? userConfigDb[0] : ""}
          />
        </div>
      )}

      {showModalEditAccount && (
        <div className="modal">
          <ModalEditAccount
            user={userDb ? userDb[0] : ""}
            onCancel={() => setShowModalEditAccount(false)}
          />
        </div>
      )}

      <LinksList>
        {isAnalyticsCompany ? (
          <ListItem>
            <NavLink
              to="/analytics"
              className={() => getActiveClassName("analytics")}
              onClick={props.handleHideNavbar}
            >
              <Icon
                src={AnalyticsIcon}
                alt="Analytics"
                style={{
                  filter:
                    "invert(100%) sepia(0%) saturate(7476%) hue-rotate(359deg) brightness(109%) contrast(106%)",
                }}
              />
              {!props.hidden && "Analytics"}
            </NavLink>
          </ListItem>
        ) : (
          <>
            {hasCookbookPermission && userDb && (
              <ListItem className="desktop">
                <NavLink
                  to="/cookbook"
                  state={{
                    openCreateCookbook: true,
                    activeNav: "createCookbook",
                  }}
                  className={() => getActiveClassName("createCookbook")}
                  onClick={props.handleHideNavbar}
                >
                  <Icon src={CreateRecipeIcon} alt="Criar receita" />
                  {!props.hidden &&
                    intl.formatMessage({ id: "menus.CRIAR RECEITA" })}
                </NavLink>
              </ListItem>
            )}
            {hasMenusPermission && (
              <ListItem className="desktop">
                <NavLink
                  to="/menu"
                  state={{ openCreateMenu: true, activeNav: "createMenu" }}
                  className={() => getActiveClassName("createMenu")}
                  onClick={props.handleHideNavbar}
                >
                  <Icon src={CreateMenuIcon} alt="Criar menu" />
                  {!props.hidden &&
                    intl.formatMessage({ id: "menus.CRIAR MENU" })}
                </NavLink>
              </ListItem>
            )}
            {(hasCookbookPermission || hasMenusPermission) && <Divider />}

            <ListItem className="desktop">
              {companyDb && (
                <NavLink
                  to={redirectHomePage(companyDb[0].companyType)}
                  className={() => getActiveClassName("home")}
                  onClick={props.handleHideNavbar}
                >
                  <Icon src={HomeIcon} alt="Home" />
                  {!props.hidden && intl.formatMessage({ id: "navbar.home" })}
                </NavLink>
              )}
            </ListItem>

            {hasCookbookPermission && (
              <ListItem className="desktop">
                <NavLink
                  to="/cookbook"
                  className={() => getActiveClassName("cookbook")}
                  onClick={props.handleHideNavbar}
                >
                  <Icon src={CookbookIcon} alt="Cookbook" />
                  {!props.hidden &&
                    intl.formatMessage({ id: "navbar.cookbook" })}
                </NavLink>
              </ListItem>
            )}
            {hasMenusPermission && (
              <ListItem className="desktop">
                <NavLink
                  to="/menu"
                  className={() => getActiveClassName("menu")}
                  onClick={props.handleHideNavbar}
                >
                  <Icon src={MenuIcon} alt="Menu" />
                  {!props.hidden &&
                    intl.formatMessage({ id: "navbar.my menus" })}
                </NavLink>
              </ListItem>
            )}
            {(hasEquipmentPermission ||
              (companyDb &&
                companyDb[0] &&
                (companyDb[0].companyType === "Suporte" ||
                  companyDb[0].companyType === "Master"))) && (
              <ListItem>
                <NavLink
                  to="/equipment"
                  className={() => getActiveClassName("equipment")}
                  onClick={props.handleHideNavbar}
                >
                  <Icon src={EquipmentIcon} alt="Equipment" />
                  {!props.hidden &&
                    intl.formatMessage({ id: "navbar.equipment" })}
                </NavLink>
              </ListItem>
            )}
            {hasUsersPermission && (
              <ListItem>
                <NavLink
                  to="/users"
                  className={() => getActiveClassName("users")}
                  onClick={props.handleHideNavbar}
                >
                  <Icon src={UserIcon} alt="Users" />
                  {!props.hidden && intl.formatMessage({ id: "navbar.users" })}
                </NavLink>
              </ListItem>
            )}
            <ListItem>
              <NavLink
                to="/information"
                className={() => getActiveClassName("information")}
                onClick={props.handleHideNavbar}
              >
                <Icon src={InfoIOK} alt="Information" />
                {!props.hidden &&
                  intl.formatMessage({ id: "navbar.information" })}
              </NavLink>
            </ListItem>
            {((userDb &&
              Number(userDb[0].userTypeId) === 1 &&
              companyDb &&
              (companyDb[0].companyType === "Prática" ||
                companyDb[0].companyType === "Suporte" ||
                companyDb[0].companyType === "Master")) ||
              (userDb && userDb[0].email) === "de@praticabr.com") && (
              <ListItem>
                <NavLink
                  to="/analytics"
                  className={() => getActiveClassName("analytics")}
                  onClick={props.handleHideNavbar}
                >
                  <Icon
                    src={AnalyticsIcon}
                    alt="Analytics"
                    style={{
                      filter:
                        "invert(100%) sepia(0%) saturate(7476%) hue-rotate(359deg) brightness(109%) contrast(106%)",
                    }}
                  />
                  {!props.hidden && "Analytics"}
                </NavLink>
              </ListItem>
            )}

            {companyDb &&
              companyDb[0].companyType === "Suporte" &&
              userDb &&
              Number(userDb[0].userTypeId) === 1 && (
                <ListItem>
                  <NavLink
                    to="/companies"
                    className={() => getActiveClassName("companies")}
                    onClick={props.handleHideNavbar}
                  >
                    <Icon src={CompaniesIcon} alt="Companies" />
                    {!props.hidden &&
                      intl.formatMessage({ id: "navbar.companhias" })}
                  </NavLink>
                </ListItem>
              )}
            {userDb &&
              userDb[0].developerToken != null &&
              (userDb[0].developerToken as string).length === 20 && (
                <ListItem id="link">
                  <a
                    href={
                      process.env.REACT_APP_EXTERNAL_API_URL + "external-api"
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon src={API} alt="External API" />
                    {!props.hidden &&
                      intl.formatMessage({ id: "navbar.API Externa" })}
                  </a>
                  {!props.hidden && <FaExternalLinkAlt id="external" />}
                </ListItem>
              )}
          </>
        )}
      </LinksList>
    </>
  );
};

export default LinksWeb;
